<mat-card
  appearance="outlined"
  class="ai-compare"
  [class.ai-compare--embedded]="embedded()"
  data-testid="ai-comparison"
>
  <mat-card-content>
    @if (!embedded()) {
      <h3 class="ai-compare__title">
        <app-icon name="sparkles" [size]="18" />
        Compare AI models
      </h3>
    }
    <p class="ai-compare__muted">
      @if (embedded()) {
        Every run, newest first, so you can compare what each model said.
      } @else {
        Re-runs a call on another model to compare its answer and price. It never changes this
        file's result, but it costs money and counts toward the AI spending limits.
      }
    </p>

    @if (loadError()) {
      <div class="sg-banner sg-banner--error" role="alert">
        <app-icon name="x-circle" [size]="18" />
        <span>Couldn't load the comparisons. Refresh the page to try again.</span>
      </div>
    } @else if (data(); as d) {
      @if (!rows().length) {
        <p class="ai-compare__muted">No completed AI call on this file to compare yet.</p>
      } @else {
        @if (!embedded()) {
          <div class="ai-compare__controls">
            @if (!purpose() || baselines().length > 1) {
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-label>Call</mat-label>
                <mat-select
                  [value]="baseline()?.short_id"
                  (valueChange)="selectedBaseline.set($event)"
                  data-testid="comparison-baseline"
                >
                  @for (b of baselines(); track b.short_id) {
                    <mat-option [value]="b.short_id">{{ baselineLabel(b) }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            }

            @if (d.can_run && baseline()) {
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-label>Model</mat-label>
                <mat-select
                  [value]="selectedModel()"
                  (valueChange)="selectedModel.set($event)"
                  data-testid="comparison-model"
                >
                  @for (m of d.models; track m.model) {
                    <mat-option [value]="m.model">{{ m.model }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <button
                mat-flat-button
                color="primary"
                type="button"
                [disabled]="!canStart()"
                (click)="start()"
                data-testid="comparison-run"
              >
                <span>Run comparison</span>
              </button>
            }
          </div>

          @if (losesThinking()) {
            <p class="ai-compare__note" data-testid="comparison-no-thinking">
              <app-icon name="info" [size]="16" />
              <span
                >This model answers without thinking first, so the model isn't the only
                change.</span
              >
            </p>
          }
          @if (refusal()) {
            <div
              class="sg-banner sg-banner--warning"
              role="status"
              data-testid="comparison-refused"
            >
              <app-icon name="alert-triangle" [size]="18" />
              <span>{{ refusal() }}</span>
            </div>
          }
        }

        <div class="ai-compare__scroll">
          <table class="ai-compare__table">
            <thead>
              <tr>
                <th scope="col">Model</th>
                <th scope="col">Answer</th>
                <th scope="col">Engine figure</th>
                @if (embedded()) {
                  <th scope="col">
                    <span class="ai-compare__said-head">What it said <app-ai-mark /></span>
                  </th>
                } @else {
                  <th scope="col">Inputs</th>
                  <th scope="col" class="ai-compare__num">Input</th>
                  <th scope="col" class="ai-compare__num">Cache read</th>
                  <th scope="col" class="ai-compare__num">Cache written</th>
                  <th scope="col" class="ai-compare__num">Output</th>
                }
                <th scope="col" class="ai-compare__num">Cost</th>
                <th scope="col" class="ai-compare__num">Time</th>
                <th scope="col"><span class="cdk-visually-hidden">Response</span></th>
              </tr>
            </thead>
            <tbody>
              @for (row of rows(); track row.short_id) {
                <tr
                  [class.ai-compare__row--live]="isCurrent(row)"
                  [class.ai-compare__row--failed]="row.status === 'FAILED' || row.abandoned"
                  data-testid="comparison-row"
                >
                  <td>
                    <span class="ai-compare__model">{{ row.model }}</span>
                    <span class="ai-compare__when">
                      @if (row.status === 'RUNNING' && !row.abandoned) {
                        In progress ·
                      } @else if (isCurrent(row)) {
                        Live run ·
                      } @else if (isLive(row)) {
                        Earlier live run ·
                      } @else {
                        Comparison ·
                      }
                      {{ row.created_at | date: 'd MMM, HH:mm' }}
                    </span>
                  </td>
                  <td>{{ answerLabel(row) }}</td>
                  <td>
                    @switch (row.engine_outcome.kind) {
                      @case ('SCORED') {
                        @if (row.engine_outcome.amount) {
                          {{ row.engine_outcome.amount | zar }}
                        } @else {
                          No figure
                        }
                      }
                      @case ('HELD') {
                        On hold
                      }
                      @case ('REFUSED') {
                        Refused
                      }
                      @default {
                        —
                      }
                    }
                  </td>
                  @if (embedded()) {
                    <td class="ai-compare__said-cell" data-testid="comparison-said">
                      <span class="ai-compare__said">{{
                        row.is_stub ? '' : row.summary || '—'
                      }}</span>
                    </td>
                  } @else {
                    <td>{{ sameInputLabel(row) }}</td>
                    <td class="ai-compare__num">{{ row.input_tokens | number }}</td>
                    <td class="ai-compare__num">{{ row.cache_read_input_tokens | number }}</td>
                    <td class="ai-compare__num">{{ row.cache_creation_input_tokens | number }}</td>
                    <td class="ai-compare__num">{{ row.output_tokens | number }}</td>
                  }
                  <td class="ai-compare__num">
                    {{ row.status === 'RUNNING' ? '—' : (row.cost_zar | zar) }}
                  </td>
                  <td class="ai-compare__num">
                    {{ row.status === 'RUNNING' ? '—' : duration(row.latency_ms) }}
                  </td>
                  <td>
                    @if (row.status !== 'RUNNING') {
                      <button mat-button type="button" (click)="view(row)">View</button>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        <p class="ai-compare__foot">
          <strong data-testid="comparison-total"
            >Total for these runs: {{ totalCost() | zar }}.</strong
          >
          A model paying to write the cache costs more than one reading it, so compare cost
          alongside the token split. Costs are list price; ZAR is a convenience conversion.
        </p>
      }
    } @else {
      <p class="ai-compare__muted">Loading…</p>
    }
  </mat-card-content>
</mat-card>
